import { observer } from "mobx-react-lite";
import { DeleteCollectionsModalStore } from "@/components/modal/delete-collections/delete-collections-modal-store";
import { pluralize } from "@/modules/pluralize";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { useModalTitleLimit } from "@/components/modal/modal-title-limits/modal-title-limits";
import {
  MDSButton,
  MDSButtonGroup,
  MDSButtonVariant,
  MDSModalDialog,
  MDSDialogSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSDialogTextContent,
  MDSDialogChip,
  MDSDialogChipVariant,
} from "@/mds";
import styled from "@emotion/styled";

interface Props {
  state: DeleteCollectionsModalStore;
}

export const DeleteCollectionsModal = observer<Props>(({ state }) => {
  const primaryTitle = state.collections[0]?.label || "";
  const secondaryTitle = getSecondaryTitle(state.collections);
  const sharedCollections = state.collections.filter(collection => collection.isShared);
  const isAllShared = sharedCollections.length === state.collections.length;
  const isAnyShared = sharedCollections.length > 0;
  const prefix =
    isAnyShared && (isAllShared || state.collections.length === 1) ? "Delete shared" : "Delete";

  const title = useModalTitleLimit({ primaryTitle, secondaryTitle, isAllShared: false });

  if (!state.collections.length) return null;

  return (
    <MDSModalDialog isOpen={state.isOpen} handleClose={() => state.close()}>
      <Title slot={MDSDialogSlotName.Header} variant={MDSTextVariant.HeaderL}>
        <span>{prefix}</span>
        <MDSDialogChip
          inline
          label={title}
          variant={MDSDialogChipVariant.Collection}
          collectionId={state.collections[0].id}
        />
        {secondaryTitle}
        <span>?</span>
      </Title>

      <MDSDialogTextContent slot={MDSDialogSlotName.Content}>
        <MDSText variant={MDSTextVariant.ParagraphS}>
          {isAnyShared ? (
            <>
              {!isAllShared && <span>Some collections you selected were shared with others. </span>}
              If you delete{" "}
              {pluralize(state.collections.length, "this collection", "these collections")},
              collaborators will immediately lose access to{" "}
              {pluralize(state.collections.length, "it", "them")}.{" "}
            </>
          ) : (
            <>
              Make sure you want to permanently delete{" "}
              {pluralize(state.collections.length, "this collection", "these collections")}.{" "}
            </>
          )}
        </MDSText>
        <MDSText variant={MDSTextVariant.ParagraphS} weight={MDSTextWeight.SemiBold}>
          You cannot undo this action.
        </MDSText>
      </MDSDialogTextContent>

      <MDSButtonGroup slot={MDSDialogSlotName.ButtonsRight}>
        <MDSButton variant={MDSButtonVariant.Default} onClick={() => state.close()}>
          Cancel
        </MDSButton>
        <MDSButton
          variant={MDSButtonVariant.DangerFilled}
          onClick={async () => await state.handleSubmit()}
        >
          Delete {pluralize(state.collections.length, "collection", "collections")}
        </MDSButton>
      </MDSButtonGroup>
    </MDSModalDialog>
  );
});

const getSecondaryTitle = (collections: CollectionObservable[]) => {
  if (collections.length === 1) return "";

  return `and ${collections.length - 1} other ${pluralize(collections.length - 1, "collection", "collections")}`;
};

const Title = styled(MDSText)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
`;
